// ✅ THEME-ONLY UPDATE: AdminFeedbackAnalytics -> match SAME "above theme" (StudentAttendanceByStudent / AdminFeeManagement)
// - Adds pageWrapSx + rootPaperSx (transparent shell)
// - Header uses headerSx (orange gradient + same typography)
// - Body uses bodySx (BG)
// - Sections use sectionSx with LEFT orange stripe + sectionInnerPadSx
// - Inputs use inputSx (focused label ORANGE)
// - Buttons use primaryBtnSx + outlinedBtnSx (same look)
// - Cards use KPI card style (white surface + subtle blue border)
// - Accordions styled like theme surfaces
// - Alerts rounded + subtle blue border
// ✅ LOGIC UNCHANGED (no API changes)

import React from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Card,
  CardContent,
  Skeleton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { API_URL } from "./config";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// --- token helpers
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
function getToken() {
  return localStorage.getItem("auth");
}
function getCampusFromToken() {
  const t = getToken();
  if (!t) return "";
  const p = decodeJwtPayload(t);
  return p?.campusCode || p?.CampusCode || "";
}

export default function AdminFeedbackAnalytics() {
  const [err, setErr] = React.useState("");
  const [loadingForms, setLoadingForms] = React.useState(false);

  // ✅ separate loading flags for auto-fetch
  const [loadingSummary, setLoadingSummary] = React.useState(false);
  const [loadingQ, setLoadingQ] = React.useState(false);

  const [filters, setFilters] = React.useState({
    CampusCode: getCampusFromToken(),
    FormID: "",
    From: "",
    To: "",
  });

  const [forms, setForms] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [qData, setQData] = React.useState(null);

  // ✅ Theme constants (match report pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";

  const LEFT_LABEL_W = 420;
  const CHART_MARGIN = { top: 10, right: 20, bottom: 10, left: 0 };

  // ---- theme sx ----
  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const sectionInnerPadSx = { pl: 1 };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
    whiteSpace: "nowrap",
  };

  const kpiCardSx = {
    ...surfaceSx,
    height: "100%",
    "& .MuiCardContent-root": { p: 2 },
  };

  const chipSx = {
    borderRadius: 2,
    backgroundColor: "rgba(25,118,210,0.06)",
    border: "1px solid rgba(25,118,210,0.18)",
    fontWeight: 800,
  };

  const accordionSx = {
    ...surfaceSx,
    overflow: "hidden",
    "&:before": { display: "none" },
  };

  const accordionSummarySx = {
    backgroundColor: "rgba(25,118,210,0.08)",
    borderBottom: "1px solid rgba(25,118,210,0.12)",
    "& .MuiAccordionSummary-content": { alignItems: "center" },
  };

  const paperItemSx = {
    ...surfaceSx,
    p: 2,
  };

  // ---- data loaders (unchanged) ----
  const loadForms = async () => {
    setErr("");
    setLoadingForms(true);

    const token = getToken();
    if (!token) {
      setErr("Session expired. Please login again.");
      setLoadingForms(false);
      return;
    }

    try {
      const resp = await axios.get(`${API_URL}/api/feedbackadmin/forms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(resp.data)
        ? resp.data
        : resp.data?.rows || resp.data?.data || [];
      setForms(Array.isArray(list) ? list : []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load forms");
      setForms([]);
    } finally {
      setLoadingForms(false);
    }
  };

  const loadSummary = async (override = {}) => {
    setErr("");
    setLoadingSummary(true);

    const token = getToken();
    if (!token) {
      setErr("Session expired. Please login again.");
      setLoadingSummary(false);
      return;
    }

    const f = { ...filters, ...override };

    try {
      const resp = await axios.get(`${API_URL}/api/feedback/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          CampusCode: f.CampusCode || undefined,
          FormID: f.FormID || undefined,
          From: f.From || undefined,
          To: f.To || undefined,
        },
      });
      setSummary(resp.data || null);
    } catch (e) {
      setSummary(null);
      setErr(e?.response?.data?.message || "Failed to load analytics");
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadQuestions = async (override = {}) => {
    setErr("");
    setLoadingQ(true);

    const token = getToken();
    if (!token) {
      setErr("Session expired. Please login again.");
      setLoadingQ(false);
      return;
    }

    const f = { ...filters, ...override };

    if (!f.FormID) {
      setQData(null);
      setLoadingQ(false);
      return;
    }

    try {
      const resp = await axios.get(`${API_URL}/api/feedback/analytics/questions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          CampusCode: f.CampusCode || undefined,
          FormID: f.FormID,
          From: f.From || undefined,
          To: f.To || undefined,
        },
      });
      setQData(resp.data || null);
    } catch (e) {
      setQData(null);
      setErr(e?.response?.data?.message || "Failed to load question analytics");
    } finally {
      setLoadingQ(false);
    }
  };

  // ✅ initial load
  React.useEffect(() => {
    loadForms();
  }, []);

  // ✅ auto load when FormID changes
  React.useEffect(() => {
    if (!filters.FormID) {
      setSummary(null);
      setQData(null);
      setAiGist(null);
      setAiMeta(null);
      return;
    }
    loadSummary({ FormID: filters.FormID });
    loadQuestions({ FormID: filters.FormID });
    loadLatestAiGist({ FormID: filters.FormID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.FormID]);

  // ✅ auto refresh when date changes
  React.useEffect(() => {
    if (!filters.FormID) return;
    loadSummary();
    loadQuestions();
    loadLatestAiGist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.From, filters.To]);

  const avgRatingChart = React.useMemo(() => {
    const list = qData?.ratingQuestions || [];
    return list
      .filter((q) => q.avg != null)
      .map((q) => ({
        name: q.QuestionText,
        avg: Number(q.avg),
        count: q.count || 0,
      }));
  }, [qData]);

  const isBusy = loadingForms || loadingSummary || loadingQ;

  const [loadingAi, setLoadingAi] = React.useState(false);
  const [aiGist, setAiGist] = React.useState(null);
  const [aiMeta, setAiMeta] = React.useState(null);
  const [snack, setSnack] = React.useState({ open: false, msg: "", type: "success" });

  const handleGenerateAiGist = async () => {
    if (!filters.FormID) return;

    setErr("");
    setLoadingAi(true);

    const token = getToken();
    if (!token) {
      setErr("Session expired. Please login again.");
      setLoadingAi(false);
      return;
    }

    try {
      const r = await axios.post(
        `${API_URL}/api/ai/feedback/${filters.FormID}/generate-gist`,
        {
          CampusCode: filters.CampusCode || undefined,
          From: filters.From || undefined,
          To: filters.To || undefined,
          GenerationMode: "manual",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAiGist(r.data?.gist || null);
      setSnack({ open: true, msg: "AI gist generated and saved ✅", type: "success" });
    } catch (e) {
      setSnack({ open: true, msg: e?.response?.data?.error || "AI gist failed", type: "error" });
    } finally {
      setLoadingAi(false);
    }
  };

  const loadLatestAiGist = async (override = {}) => {
  const token = getToken();
  if (!token) return;

  const f = { ...filters, ...override };
  if (!f.FormID) {
    setAiGist(null);
    setAiMeta(null);
    return;
  }

  try {
      const resp = await axios.get(`${API_URL}/api/ai/feedback/${f.FormID}/latest`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          CampusCode: f.CampusCode || undefined,
          From: f.From || undefined,
          To: f.To || undefined,
        },
      });

      if (resp.data?.exists) {
        setAiGist(resp.data.gist || null);
        setAiMeta(resp.data.meta || null);
      } else {
        setAiGist(null);
        setAiMeta(null);
      }
    } catch {
      // don't block analytics page if AI fetch fails
      setAiGist(null);
      setAiMeta(null);
    }
  };


  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Feedback Analytics
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Select a form to auto-load Summary + Question insights
            </Typography>
          </Box>

        </Box>

        {/* ✅ Body */}
        <Box sx={bodySx}>
          {err ? (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
            >
              {err}
            </Alert>
          ) : null}

          {/* Filters (stripe section) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Feedback Form"
                    value={filters.FormID}
                    onChange={(e) => setFilters((p) => ({ ...p, FormID: e.target.value }))}
                    disabled={loadingForms}
                    sx={inputSx}
                  >
                    <MenuItem value="">Select a form</MenuItem>
                    {forms.map((f) => (
                      <MenuItem key={f.FormID} value={String(f.FormID)}>
                        {f.FormID} • {f.Title}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From"
                    value={filters.From}
                    onChange={(e) => setFilters((p) => ({ ...p, From: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    disabled={!filters.FormID}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="To"
                    value={filters.To}
                    onChange={(e) => setFilters((p) => ({ ...p, To: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    disabled={!filters.FormID}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>

              {isBusy ? (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading analytics…
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Paper>

          {/* If no Form selected */}
          {!filters.FormID ? (
            <Paper variant="outlined" sx={sectionSx}>
              <Box sx={sectionInnerPadSx}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Please select a feedback form
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Summary and question-wise analytics will appear automatically.
                </Typography>
              </Box>
            </Paper>
          ) : (
            <>
              {/* ✅ Summary */}
              <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
                <Box sx={sectionInnerPadSx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                      Summary
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {loadingSummary ? "Refreshing…" : "Up to date"}
                    </Typography>
                  </Box>

                  {!summary && loadingSummary ? (
                    <Grid container spacing={2}>
                      {[1, 2, 3, 4].map((k) => (
                        <Grid key={k} item xs={12} md={3}>
                          <Skeleton variant="rounded" height={86} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : !summary ? (
                    <Typography color="text.secondary">No summary available.</Typography>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Card elevation={0} sx={kpiCardSx}>
                          <CardContent>
                            <Typography variant="caption" color="text.secondary">
                              Total Responses
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: THEME_ORANGE, mt: 0.5 }}>
                              {summary.totalResponses ?? 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Card elevation={0} sx={kpiCardSx}>
                          <CardContent>
                            <Typography variant="caption" color="text.secondary">
                              Avg Overall Rating
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: THEME_ORANGE, mt: 0.5 }}>
                              {summary.avgOverallRating == null
                                ? "-"
                                : Number(summary.avgOverallRating).toFixed(2)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={kpiCardSx}>
                          <CardContent>
                            <Typography variant="caption" color="text.secondary">
                              Top Categories
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              {(summary.topCategories || []).length ? (
                                summary.topCategories.map((x, idx) => (
                                  <Typography key={idx} variant="body2">
                                    {x.Category} — {x.Count}
                                  </Typography>
                                ))
                              ) : (
                                <Typography variant="body2">-</Typography>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12}>
                        <Card elevation={0} sx={kpiCardSx}>
                          <CardContent>
                            <Typography variant="caption" color="text.secondary">
                              Overall Rating Distribution
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              {(summary.ratingDistribution || []).length ? (
                                summary.ratingDistribution.map((x, idx) => (
                                  <Typography key={idx} variant="body2">
                                    {x.Rating}: {x.Count}
                                  </Typography>
                                ))
                              ) : (
                                <Typography variant="body2">-</Typography>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Paper>
              
              {/* ✅ AI Gist */}
              <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
                <Box sx={sectionInnerPadSx}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                      AI Gist
                    </Typography>

                    <Button
                      variant="contained"
                      startIcon={loadingAi ? <CircularProgress size={18} sx={{ color: "white" }} /> : <AutoAwesomeIcon />}
                      onClick={handleGenerateAiGist}
                      disabled={loadingAi || !filters.FormID}
                      sx={primaryBtnSx}
                    >
                      {loadingAi ? "Generating…" : "Generate AI Gist"}
                    </Button>
                    </Box>

                    {aiMeta?.GeneratedAt ? (
                      <Typography variant="caption" color="text.secondary">
                        Last generated: {new Date(aiMeta.GeneratedAt).toLocaleString()} • {aiMeta.GenerationMode}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No AI summary saved for this filter yet.
                      </Typography>
                    )}
                  </Box>
                  
                  {!aiGist ? (
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      Click “Generate AI Gist” to create an executive summary and action plan.
                    </Typography>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontWeight: 800, mb: 1 }}>
                        {aiGist.gist}
                      </Typography>
                  
                      {!!(aiGist.positives || []).length && (
                        <>
                          <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>Positives</Typography>
                          {(aiGist.positives || []).map((x, i) => (
                            <Typography key={i} variant="body2">• {x}</Typography>
                          ))}
                        </>
                      )}

                      {!!(aiGist.concerns || []).length && (
                        <>
                          <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>Concerns</Typography>
                          {(aiGist.concerns || []).map((x, i) => (
                            <Typography key={i} variant="body2">• {x}</Typography>
                          ))}
                        </>
                      )}

                      {!!(aiGist.actionable_recommendations || []).length && (
                        <>
                          <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>Action Plan</Typography>
                          {(aiGist.actionable_recommendations || []).map((x, i) => (
                            <Typography key={i} variant="body2">{i + 1}. {x}</Typography>
                          ))}
                        </>
                      )}

                      {aiGist.confidence_note ? (
                        <Alert severity="info" sx={{ mt: 1, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}>
                          {aiGist.confidence_note}
                        </Alert>
                      ) : null}
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* ✅ Question Analytics */}
              <Box>
                {/* Avg Rating by Question */}
                {(avgRatingChart || []).length ? (
                  <Paper variant="outlined" sx={{ ...sectionSx, mb: 2, p: 2 }}>
                    <Box sx={sectionInnerPadSx}>
                      <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                        Avg Rating by Question
                      </Typography>

                      <ResponsiveContainer width="100%" height={Math.max(380, avgRatingChart.length * 70)}>
                        <BarChart data={avgRatingChart} layout="vertical" margin={CHART_MARGIN}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 5]} />
                          <YAxis type="category" dataKey="name" width={LEFT_LABEL_W} tick={{ fontSize: 12 }} />
                          <Tooltip
                            formatter={(val, name) => {
                              if (name === "avg") return [Number(val).toFixed(2), "Avg Rating"];
                              return [val, name];
                            }}
                            labelFormatter={(label) => `Question: ${label}`}
                          />
                          <Bar dataKey="avg" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={paperItemSx}>
                    <Typography color="text.secondary">No rating questions (or no rating answers yet).</Typography>
                  </Paper>
                )}

                {/* MCQ Questions */}
                <Accordion defaultExpanded sx={{ ...accordionSx, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                      MCQ Questions
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ backgroundColor: "#fff" }}>
                    {loadingQ && !qData ? (
                      <Box sx={{ p: 1 }}>
                        <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={200} />
                      </Box>
                    ) : (qData?.mcqQuestions || []).length ? (
                      qData.mcqQuestions.map((q, idx) => (
                        <Paper
                          key={q.QuestionID}
                          variant="outlined"
                          sx={{
                            ...sectionSx,
                            mb: 2,
                            p: 2,
                            backgroundColor: idx % 2 ? ROW_ALT : "#fff",
                            "&:hover": { backgroundColor: ROW_ALT },
                          }}
                        >
                          <Box sx={sectionInnerPadSx}>
                            <Typography sx={{ fontWeight: 900, mb: 1, color: "rgba(25,118,210,0.95)" }}>
                              {q.QuestionText}
                            </Typography>

                            <ResponsiveContainer width="100%" height={Math.max(260, (q.options || []).length * 55)}>
                              <BarChart
                                data={(q.options || []).map((o) => ({ option: o.OptionText, count: o.count }))}
                                layout="vertical"
                                margin={CHART_MARGIN}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" allowDecimals={false} />
                                <YAxis type="category" dataKey="option" width={LEFT_LABEL_W} tick={{ fontSize: 12 }} />
                                <Tooltip
                                  formatter={(val) => [val, "Responses"]}
                                  labelFormatter={(label) => `Option: ${label}`}
                                />
                                <Bar dataKey="count" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">No MCQ questions.</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Text Questions */}
                <Accordion defaultExpanded sx={accordionSx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                      Text Questions
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ backgroundColor: "#fff" }}>
                    {(qData?.textQuestions || []).length ? (
                      qData.textQuestions.map((q, qi) => (
                        <Paper
                          key={q.QuestionID}
                          variant="outlined"
                          sx={{
                            ...sectionSx,
                            mb: 2,
                            p: 2,
                            backgroundColor: qi % 2 ? ROW_ALT : "#fff",
                            "&:hover": { backgroundColor: ROW_ALT },
                          }}
                        >
                          <Box sx={sectionInnerPadSx}>
                            <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                              {q.QuestionText}
                            </Typography>

                            {!!(q.topKeywords || []).length && (
                              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
                                {q.topKeywords.map((k, idx) => (
                                  <Chip key={idx} label={`${k.word} (${k.count})`} sx={{ ...chipSx, mb: 1 }} />
                                ))}
                              </Stack>
                            )}

                            <Box sx={{ mt: 1, maxHeight: 260, overflow: "auto" }}>
                              {(q.latest || []).length ? (
                                q.latest.map((x, idx) => (
                                  <Paper
                                    key={idx}
                                    variant="outlined"
                                    sx={{
                                      p: 1,
                                      mb: 1,
                                      borderRadius: 2,
                                      borderColor: "rgba(25,118,210,0.18)",
                                      backgroundColor: idx % 2 ? "rgba(25,118,210,0.04)" : "#fff",
                                    }}
                                  >
                                    <Typography variant="body2">{x.text}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(x.submittedAt).toLocaleString()}
                                    </Typography>
                                  </Paper>
                                ))
                              ) : (
                                <Typography color="text.secondary">No text responses.</Typography>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">No text questions.</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
